import AgGridReactTable from "@components/ag-grid/AgGridReactTable";
import DataPill from "@components/pills/DataPill";
import AgGridSkeleton from "@components/ag-grid/AgGridSkeleton";
import CopyButton from "@components/buttons/CopyButton";
import LightCustomButton from "@components/buttons/LightCustomButton";
import GlobalSearchInputField from "@components/inputs/GlobalSearchInputField";
import KycDumpSlider from "@components/sliders/KycDumpSlider";
import { cashierApis } from "@services/apis/Cashier";
import {
  GetCashierListData,
  GetCashierListModel,
} from "@services/models/Cashier/GetCashierListModel";
import { KycData } from "@utils/clxm/kycdata";
import { ColDef, RowClickedEvent } from "ag-grid-community";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ConstantFunctions from "../../utils/constants_functions";
import { CSV_EXPORT_CONSTANTS } from "../../utils/csvExportConstants";
import CashierDetails from "./CashierDetails";
import AddCashierSlider from "./components/AddCashierSlider";

const CashierManagement = () => {
  const navigate = useNavigate();
  const constantFunctions = new ConstantFunctions();
  const gridRef = useRef<any>(null);
  const loadingRef = useRef<boolean>(false);

  const [cashiers, setCashiers] = useState<GetCashierListData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [appliedSearchTerm, setAppliedSearchTerm] = useState<string>("");
  const [userId, setUserId] = useState<string | undefined>(undefined);
  const [exportLoading, setExportLoading] = useState<boolean>(false);

  const [addCashierSlider, setAddCashierSlider] = useState<boolean>(false);

  const [isKycDumpOpen, setIsKycDumpOpen] = useState(false);
  const [kycData, setKycData] = useState<KycData>({
    idDump: undefined,
    poaDump: undefined,
    poiFrontUrl: "",
    poaFrontUrl: "",
    poaBackUrl: "",
  });

  const [isPanelCollapsed, setIsPanelCollapsed] = useState<boolean>(false);

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "UID",
        field: "userId",
        flex: 0.6,
        minWidth: 60,
        maxWidth: 100,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
        cellRenderer: (props: any) => {
          const { data } = props;
          return (
            <div className="flex items-center justify-between">
              <span className="flex-1 pr-1">{data.userId}</span>
              <CopyButton
                copyText={data.userId.toString()}
                copyMessage="User ID copied!"
                buttonSize="14px"
                copyIconSize={10}
                borderRadius="2px"
              />
            </div>
          );
        },
      },
      {
        headerName: "Online Status",
        field: "status",
        flex: 0.8,
        minWidth: 100,
        maxWidth: 140,
        cellRenderer: (props: any) => {
          const { data } = props;
          const isOnline = data.status !== "offline";
          return (
            <DataPill
              label={isOnline ? "ON" : "OFF"}
              tone={isOnline ? "green" : "red"}
            />
          );
        },
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Username",
        field: "username",
        flex: 1.2,
        minWidth: 130,
        maxWidth: 180,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Email",
        field: "emailId",
        flex: 1.8,
        minWidth: 180,
        maxWidth: 260,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: (props: any) => {
          const { data } = props;
          return (
            <div className="flex items-center justify-between">
              <span className="flex-1 truncate pr-1">{data.emailId}</span>
              <CopyButton
                copyText={data.emailId}
                copyMessage="Email copied!"
                buttonSize="14px"
                copyIconSize={10}
                borderRadius="2px"
              />
            </div>
          );
        },
      },
      {
        headerName: "Contact Number",
        field: "whatsappNumber",
        flex: 1.2,
        minWidth: 130,
        maxWidth: 180,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Wallet",
        field: "wallet",
        flex: 1,
        minWidth: 100,
        maxWidth: 150,
        cellRenderer: (props: any) => {
          const { data } = props;
          return constantFunctions.formatCurrency(Number(data.wallet));
        },
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
        sortable: true,
        comparator: (valueA: string, valueB: string) => {
          return Number(valueA) - Number(valueB);
        },
      },
      {
        headerName: "Escrow",
        field: "escrow",
        flex: 1,
        minWidth: 100,
        maxWidth: 150,
        cellRenderer: (props: any) => {
          const { data } = props;
          return constantFunctions.formatCurrency(Number(data.escrow));
        },
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
        sortable: true,
        comparator: (valueA: string, valueB: string) => {
          return Number(valueA) - Number(valueB);
        },
      },
      {
        headerName: "Total",
        field: "total",
        flex: 1,
        minWidth: 100,
        maxWidth: 150,
        cellRenderer: (props: any) => {
          const { data } = props;
          return constantFunctions.formatCurrency(Number(data.total));
        },
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
        sortable: true,
        comparator: (valueA: string, valueB: string) => {
          return Number(valueA) - Number(valueB);
        },
      },
      {
        headerName: "Status",
        field: "cashierState",
        flex: 0.9,
        minWidth: 100,
        maxWidth: 140,
        cellRenderer: (props: any) => {
          const { data } = props;
          const status =
            data.cashierState === 0
              ? "deleted"
              : data.cashierState === 1
              ? "active"
              : "suspended";
          return <DataPill value={status} />;
        },
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "KYC",
        field: "kyc",
        flex: 0.9,
        minWidth: 100,
        maxWidth: 140,
        cellRenderer: (props: any) => {
          const { data } = props;
          const hasKycData = !!(
            data.poiDump ||
            data.poaDump ||
            data.poiImageUrl ||
            data.poaImageFrontUrl ||
            data.poaImageBackUrl
          );
          return (
            <LightCustomButton
              text="VIEW"
              textSize="10px"
              isActive={hasKycData}
              onClick={() => {
                setIsKycDumpOpen(true);
                setKycData({
                  idDump: data.poiDump,
                  poaDump: data.poaDump,
                  poiFrontUrl: data.poiImageUrl,
                  poaFrontUrl: data.poaImageFrontUrl,
                  poaBackUrl: data.poaImageBackUrl,
                });
              }}
              className="!h-[24px] !flex-none w-full flex items-center justify-center"
            />
          );
        },
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: false,
        sortable: false,
      },
    ],
    [constantFunctions]
  );

  const onRowClicked = (event: RowClickedEvent) => {
    const selectedCashier = event.data as GetCashierListData;

    const allRows = document.querySelectorAll(".ag-row");
    allRows.forEach((row) => {
      row.classList.remove("ag-row-selected-custom");
    });

    if (event.rowIndex !== null) {
      const rowElement = document.querySelector(
        `[row-index="${event.rowIndex}"]`
      );
      if (rowElement) {
        rowElement.classList.add("ag-row-selected-custom");
      }
    }

    setUserId(selectedCashier.userId.toString());
    setIsPanelCollapsed(false);
  };
  const fetchCashierList = useCallback(async (search?: string) => {
    if (loadingRef.current) return;

    try {
      loadingRef.current = true;
      setLoading(true);
      setUserId(undefined);

      const response: GetCashierListModel = await cashierApis.getCashierList({
        page: 1,
        rows: 10000,
        search: search,
      });

      setCashiers(response.data);
    } catch (err: any) {
      setError("Failed to fetch cashier data");
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCashierList(appliedSearchTerm || undefined);
  }, [appliedSearchTerm, fetchCashierList]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleSearch = useCallback(() => {
    const normalizedSearchTerm = searchTerm.trim();

    if (normalizedSearchTerm === appliedSearchTerm) {
      fetchCashierList(normalizedSearchTerm || undefined);
      return;
    }

    setAppliedSearchTerm(normalizedSearchTerm);
  }, [appliedSearchTerm, fetchCashierList, searchTerm]);

  const handleClearSearch = useCallback(() => {
    if (!searchTerm && !appliedSearchTerm) {
      return;
    }

    setSearchTerm("");

    if (appliedSearchTerm === "") {
      fetchCashierList();
      return;
    }

    setAppliedSearchTerm("");
  }, [appliedSearchTerm, fetchCashierList, searchTerm]);

  const handleAddCashierClose = useCallback(() => {
    setAddCashierSlider(false);
    fetchCashierList(appliedSearchTerm || undefined);
  }, [appliedSearchTerm, fetchCashierList]);

  const handleAddCashierOpen = useCallback(() => {
    setAddCashierSlider(true);
  }, []);

  const exportToCsv = async () => {
    try {
      setExportLoading(true);

      const { MAX_ROWS, ROWS_PER_PAGE, TOTAL_PAGES } = CSV_EXPORT_CONSTANTS;
      let allCashiers: GetCashierListData[] = [];

      for (let i = 1; i <= TOTAL_PAGES; i++) {
        const response: GetCashierListModel = await cashierApis.getCashierList({
          page: i,
          rows: ROWS_PER_PAGE,
          search: appliedSearchTerm,
        });

        allCashiers = [...allCashiers, ...response.data];

        if (
          response.data.length < ROWS_PER_PAGE ||
          allCashiers.length >= MAX_ROWS
        ) {
          break;
        }
      }

      allCashiers = allCashiers.slice(0, MAX_ROWS);

      const headers = [
        "UID",
        "Username",
        "Email",
        "Contact Number",
        "Wallet",
        "Escrow",
        "Total",
        "Status",
        "Online Status",
      ];

      let csvContent = headers.join(",") + "\n";

      allCashiers.forEach((cashier) => {
        const status =
          cashier.cashierState === 0
            ? "DELETED"
            : cashier.cashierState === 1
            ? "ACTIVE"
            : "SUSPENDED";

        const onlineStatus = cashier.status === "offline" ? "OFF" : "ON";

        const row = [
          cashier.userId,
          `"${(cashier.username || "").replace(/"/g, '""')}"`,
          `"${(cashier.emailId || "").replace(/"/g, '""')}"`,
          `"${cashier.whatsappNumber || ""}"`,
          Number(cashier.wallet),
          Number(cashier.escrow),
          Number(cashier.total),
          status,
          onlineStatus,
        ];

        csvContent += row.join(",") + "\n";
      });

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);

      const date = new Date();
      const formattedDate = `${date.getFullYear()}-${String(
        date.getMonth() + 1
      ).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

      link.setAttribute("download", `cashier-data-${formattedDate}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Failed to export data", error);
      setError("Failed to export data");
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <>
      <div className="h-[70px] border-b border-border-subtle bg-bg-page-body flex flex-row sticky top-0 bg-white z-10 pl-[20px] w-full">
        <div className="flex flex-row justify-between items-center w-full mr-4">
          <p className="flex text-text-base text-[14px] font-semibold items-center">
            Cashier Management
          </p>
          <div className="flex flex-row gap-[12px] items-center">
            <LightCustomButton
              text="AUTO TRADE UPIs"
              isActive={true}
              onClick={() => navigate("/cashier-management/auto-trade-upis")}
            />
            <LightCustomButton
              text="+ ADD CASHIER"
              isActive={true}
              onClick={handleAddCashierOpen}
            />
            <LightCustomButton
              text={"EXPORT CSV"}
              isActive={true}
              onClick={exportToCsv}
              isButtonLoading={exportLoading}
            />
            <GlobalSearchInputField
              value={searchTerm}
              onChange={handleSearchChange}
              onSearch={handleSearch}
              onClear={handleClearSearch}
              disabled={loading}
              isSearchApplied={Boolean(appliedSearchTerm)}
              placeholder="Search Cashier..."
            />
          </div>
        </div>
      </div>
      <div className="flex flex-row items-start">
        <div
          className={`flex flex-col bg-bg-page-body py-[20px] px-[12px] overflow-hidden ${
            userId && !isPanelCollapsed ? "w-3/4" : "w-full"
          }`}
          style={{ height: "calc(100dvh - 70px)" }}
        >
          {loading && cashiers.length === 0 ? (
            <AgGridSkeleton
              columnDefs={columnDefs}
              style={{ height: "100%", width: "100%" }}
            />
          ) : (
            <AgGridReactTable
              ref={gridRef}
              rowData={cashiers}
              columnDefs={columnDefs}
              onRowClicked={onRowClicked}
              getRowId={(params) => params.data.userId.toString()}
              error={error}
            />
          )}
          <AddCashierSlider
            isOpen={addCashierSlider}
            onClose={handleAddCashierClose}
          />
        </div>
        {(kycData.idDump ||
          kycData.poaDump ||
          kycData.poiFrontUrl ||
          kycData.poaFrontUrl ||
          kycData.poaBackUrl) && (
          <KycDumpSlider
            isOpen={isKycDumpOpen}
            onClose={() => setIsKycDumpOpen(false)}
            kycDumpData={kycData}
          />
        )}
        {userId && (
          <div
            className={`bg-bg-page-body border-l border-border-subtle transition-all duration-300 relative ${
              isPanelCollapsed ? "w-0 px-0" : "w-1/4 px-2"
            }`}
            style={{ height: "calc(100dvh - 70px)" }}
          >
            <button
              onClick={() => setIsPanelCollapsed(!isPanelCollapsed)}
              className="absolute top-1/2 -translate-y-1/2 z-20 bg-bg-button-secondary text-text-base rounded-lg shadow-lg transition-all duration-200 flex items-center justify-center group"
              style={{
                left: isPanelCollapsed ? "calc(100% - 32px)" : "-16px",
                width: "32px",
                height: "60px",
              }}
              aria-label={isPanelCollapsed ? "Expand panel" : "Collapse panel"}
            >
              <span className="material-symbols-outlined text-[20px] group-hover:scale-110 transition-transform">
                {isPanelCollapsed ? "chevron_left" : "chevron_right"}
              </span>
            </button>

            <div
              className={`overflow-auto h-full transition-opacity duration-300 ${
                isPanelCollapsed
                  ? "opacity-0 pointer-events-none"
                  : "opacity-100"
              }`}
            >
              <CashierDetails userId={Number(userId)} />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CashierManagement;
